import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Package, Check, Truck, RefreshCcw, Shirt, Zap, Settings } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const H = { fontFamily: 'Bebas Neue, sans-serif' };

const TIERS = [
  {
    key: 'standard', name: 'Standard Box', price: 39,
    blurb: 'The monthly essentials drop.',
    includes: ['1 NinjAthlete apparel piece (tee or hat)', '1 training accessory (bands, grips, wraps…)', 'Supplement & recovery samples', 'Member-only drop discount codes'],
  },
  {
    key: 'elite', name: 'Elite Box', price: 79, popular: true,
    blurb: 'The full athlete loadout.',
    includes: ['1 premium apparel piece (hoodie, jacket or limited tee)', '2 training accessories', '1 full-size recovery item (shaker, roller, plunge gear…)', 'Supplement & recovery samples', 'Early access to every merch drop'],
  },
];

const FAQS = [
  ['When does my box ship?', 'Boxes ship the first week of every month to the address you enter at checkout. US shipping only for now.'],
  ['Can I cancel or pause?', 'Any time. Hit "Manage subscription" below — cancellations take effect at the end of your current billing period. No lock-in, no fees.'],
  ['What sizes do you stock?', 'After checkout we email you a quick size preference form (S–2XL). Update it any time before your next box ships.'],
  ['Is every box different?', 'Yes — no repeats within a 12-month window. Elite boxes always include at least one item not sold in the store.'],
];

const TierCard = ({ tier, onSubscribe, busy }) => (
  <div className={`border p-7 flex flex-col ${tier.popular ? 'border-[#C7FA3E]/60 bg-[#C7FA3E]/[0.04]' : 'border-zinc-800 bg-zinc-900/40'}`} data-testid={`gearbox-tier-${tier.key}`}>
    {tier.popular && <span className="text-[#C7FA3E] text-[10px] uppercase tracking-[2px] mb-2">Most Popular</span>}
    <h3 className="text-white text-3xl" style={H}>{tier.name}</h3>
    <p className="text-zinc-400 text-xs mb-3">{tier.blurb}</p>
    <p className="text-white text-4xl font-bold">${tier.price}<span className="text-zinc-500 text-sm font-normal">/mo</span></p>
    <ul className="space-y-2 my-5 flex-1">
      {tier.includes.map((i) => (
        <li key={i} className="flex items-start gap-2 text-zinc-400 text-xs"><Check className="w-3.5 h-3.5 text-[#C7FA3E] flex-shrink-0 mt-0.5" /> {i}</li>
      ))}
    </ul>
    <button onClick={() => onSubscribe(tier.key)} disabled={busy !== null}
      className="bg-[#C7FA3E] text-black text-sm font-bold uppercase tracking-wider py-3.5 disabled:opacity-40 hover:bg-[#d4ff5c] transition-colors"
      data-testid={`gearbox-subscribe-${tier.key}`}>
      {busy === tier.key ? 'Opening checkout…' : `Subscribe — $${tier.price}/mo`}
    </button>
  </div>
);

export default function GearBoxPage() {
  const [params] = useSearchParams();
  const cancelled = params.get('cancelled');
  const [email, setEmail] = useState('');
  const [pendingTier, setPendingTier] = useState(null);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState('');
  const [manageEmail, setManageEmail] = useState('');
  const [manageBusy, setManageBusy] = useState(false);
  const [manageError, setManageError] = useState('');

  useEffect(() => {
    document.title = 'NinjAthlete Gear Box — Monthly Training & Recovery Subscription';
    let m = document.querySelector('meta[name="description"]');
    if (m) m.setAttribute('content', 'A monthly box of NinjAthlete apparel, training accessories and recovery gear. Standard $39/mo or Elite $79/mo. Cancel anytime.');
  }, []);

  const startCheckout = async (tier) => {
    if (!email.trim()) { setPendingTier(tier); setError(''); document.getElementById('gearbox-email')?.focus(); return; }
    setBusy(tier);
    setError('');
    try {
      const r = await fetch(`${API}/api/gearbox/checkout`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tier, email: email.trim(), origin_url: window.location.origin }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail?.[0]?.msg || d.detail || 'Checkout failed');
      window.location.href = d.checkout_url;
    } catch (e) { setError(e.message); setBusy(null); }
  };

  const openPortal = async () => {
    if (!manageEmail.trim()) return;
    setManageBusy(true);
    setManageError('');
    try {
      const r = await fetch(`${API}/api/gearbox/portal`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: manageEmail.trim(), origin_url: window.location.origin }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail?.[0]?.msg || d.detail || 'Could not open the billing portal');
      window.location.href = d.portal_url;
    } catch (e) { setManageError(e.message); setManageBusy(false); }
  };

  return (
    <div className="bg-[#0a0a0a] min-h-screen" data-testid="gearbox-page">
      {/* Hero */}
      <header className="border-b border-zinc-800 relative overflow-hidden">
        <div className="max-w-6xl mx-auto px-5 pt-28 pb-16 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <p className="text-[#C7FA3E] text-xs uppercase tracking-[4px] mb-4 flex items-center gap-2"><Package className="w-4 h-4" /> Monthly Subscription</p>
            <h1 className="text-white text-4xl sm:text-5xl lg:text-6xl mb-5" style={H}>The NinjAthlete Gear Box</h1>
            <p className="text-zinc-300 text-base leading-relaxed mb-6 max-w-lg">
              Fresh NinjAthlete apparel, training accessories, and recovery gear — delivered monthly.
              Every box is different. Cancel anytime.
            </p>
            <div className="flex flex-wrap gap-5 text-zinc-400 text-xs">
              <span className="flex items-center gap-1.5"><Truck className="w-4 h-4 text-[#C7FA3E]" /> Ships first week of the month</span>
              <span className="flex items-center gap-1.5"><RefreshCcw className="w-4 h-4 text-[#C7FA3E]" /> Cancel anytime</span>
              <span className="flex items-center gap-1.5"><Shirt className="w-4 h-4 text-[#C7FA3E]" /> No repeat items</span>
            </div>
          </div>
          <img src="/gearbox-hero.jpg" alt="NinjAthlete Gear Box with apparel, cap and recovery gear" className="w-full border border-zinc-800 object-cover" data-testid="gearbox-hero-img" />
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-5 py-16">
        {cancelled && (
          <p className="text-amber-400 text-sm border border-amber-500/30 bg-amber-500/[0.05] px-4 py-3 mb-8" data-testid="gearbox-cancelled-note">
            Checkout cancelled — no charge was made. Pick a box whenever you're ready.
          </p>
        )}

        {/* Email + tiers */}
        <section className="mb-16" data-testid="gearbox-tiers-section">
          <h2 className="text-white text-3xl sm:text-4xl mb-2" style={H}>Pick Your Box</h2>
          <p className="text-zinc-500 text-sm mb-5">Enter your email, choose a tier, and finish on Stripe's secure checkout (shipping address collected there).</p>
          <input
            id="gearbox-email" type="email" value={email}
            onChange={(e) => { setEmail(e.target.value); if (pendingTier) setError(''); }}
            placeholder="you@example.com"
            className={`w-full max-w-md bg-zinc-900 border text-white text-sm px-4 py-3.5 outline-none mb-2 ${pendingTier && !email.trim() ? 'border-[#C7FA3E]' : 'border-zinc-700 focus:border-[#C7FA3E]'}`}
            data-testid="gearbox-email-input"
          />
          {pendingTier && !email.trim() && <p className="text-[#C7FA3E] text-xs mb-3" data-testid="gearbox-email-hint">Enter your email first, then hit subscribe again.</p>}
          {error && <p className="text-red-400 text-xs mb-3" data-testid="gearbox-error">{error}</p>}
          <div className="grid md:grid-cols-2 gap-5 mt-4 max-w-3xl">
            {TIERS.map((t) => <TierCard key={t.key} tier={t} onSubscribe={startCheckout} busy={busy} />)}
          </div>
        </section>

        {/* How it works */}
        <section className="mb-16" data-testid="gearbox-how-section">
          <h2 className="text-white text-3xl sm:text-4xl mb-5" style={H}>How It Works</h2>
          <ol className="grid sm:grid-cols-3 gap-4">
            {[
              ['1. Subscribe', 'Pick Standard or Elite and check out securely with Stripe. Shipping address is collected at checkout.'],
              ['2. We curate', 'Each month we pack a fresh mix of apparel, training accessories and recovery gear around a monthly theme.'],
              ['3. It ships', 'Boxes go out the first week of every month. Skip, pause or cancel from the billing portal whenever.'],
            ].map(([t, d]) => (
              <li key={t} className="border border-zinc-800 bg-zinc-900/40 p-5">
                <p className="text-white font-bold text-sm mb-2">{t}</p>
                <p className="text-zinc-500 text-xs leading-relaxed">{d}</p>
              </li>
            ))}
          </ol>
        </section>

        {/* FAQ */}
        <section className="mb-16" data-testid="gearbox-faq-section">
          <h2 className="text-white text-3xl sm:text-4xl mb-5" style={H}>Gear Box FAQ</h2>
          <div className="space-y-4 max-w-3xl">
            {FAQS.map(([q, a], i) => (
              <div key={i} className="border border-zinc-800 bg-zinc-900/30 p-5" data-testid={`gearbox-faq-${i}`}>
                <h3 className="text-white text-sm font-bold mb-2">{q}</h3>
                <p className="text-zinc-400 text-xs leading-relaxed">{a}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Manage subscription */}
        <section className="border border-zinc-800 bg-zinc-900/40 p-7 max-w-3xl" data-testid="gearbox-manage-section">
          <h2 className="text-white text-2xl mb-2 flex items-center gap-2" style={H}><Settings className="w-5 h-5 text-[#C7FA3E]" /> Already Subscribed?</h2>
          <p className="text-zinc-500 text-xs mb-4">Enter your subscription email to open the secure Stripe billing portal — update your card, pause, or cancel.</p>
          <div className="flex flex-wrap gap-2">
            <input type="email" value={manageEmail} onChange={(e) => setManageEmail(e.target.value)} placeholder="you@example.com"
              className="bg-zinc-900 border border-zinc-700 text-white text-sm px-4 py-3 focus:border-[#C7FA3E] outline-none w-72"
              data-testid="gearbox-manage-email" />
            <button onClick={openPortal} disabled={manageBusy || !manageEmail.trim()}
              className="border border-[#C7FA3E] text-[#C7FA3E] text-xs font-bold uppercase tracking-wider px-5 disabled:opacity-40 hover:bg-[#C7FA3E]/10 transition-colors"
              data-testid="gearbox-manage-btn">
              {manageBusy ? 'Opening…' : 'Manage subscription'}
            </button>
          </div>
          {manageError && <p className="text-red-400 text-xs mt-3" data-testid="gearbox-manage-error">{manageError}</p>}
        </section>

        <p className="text-zinc-600 text-[11px] mt-10 max-w-3xl flex items-start gap-1.5">
          <Zap className="w-3 h-3 mt-0.5 flex-shrink-0" />
          US shipping only. Billing is handled securely by Stripe; cancelling stops future boxes at the end of the current period. Box contents vary monthly and substitutions of equal or greater value may occur.
        </p>
      </div>
    </div>
  );
}
